import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Form, Formik } from 'formik';

import SaveIcon from '@mui/icons-material/Save';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'components/TextField';

import {
  PackageListAddMutation,
  PackageListAddMutation$data,
} from '__generated__/PackageListAddMutation.graphql';
import { useDialog } from 'components/Dialog';
import { useNavigate } from 'react-router';
import { useErrorHandler } from 'lib';

type FormFields = {
  name: string;
};

type Props = {
  connectionId: string;
};

const initial = {
  name: '',
};

const PackageListAdd: FC<Props> = ({ connectionId }) => {
  const { close } = useDialog();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler<PackageListAddMutation$data>();

  const [add, loading] = useMutation<PackageListAddMutation>(graphql`
    mutation PackageListAddMutation($input: CreatePackageInput!) {
      createPackage(input: $input) {
        node {
          id
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { input },

        ...errorHandler({
          onCompleted: data => {
            navigate(`/packages/${data.createPackage.node?.id}`);
          },
        }),

        updater: store => {
          store.get(connectionId)?.invalidateRecord();
        },
      });
    },
    [add, connectionId, errorHandler, navigate]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas pasiūlymas</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Kelionės pasiūlymas yra paslaugų paketas, kurį vartotojai gali
            užsakyti svetainėje.
          </DialogContentText>

          <TextField
            autoFocus
            name="name"
            label="Pasiūlymo pavadinimas"
            fullWidth
            variant="standard"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={loading}
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}>
            Saugoti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default PackageListAdd;
